import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { graphql, useStaticQuery } from 'gatsby';
import styled from '@emotion/styled';
import { useDebounce } from 'react-use';

import useUserContext from '../../../hooks/use-user-context';
import { createOptionMap, outputIfNoVariables } from '../../../utilities/misc';
import { rem } from '../../../utilities/style';
import { colors } from '../../../theme/colors';
import { media } from '../../../theme/media';
import { containerStyles } from '../../../styles/layout';
import { headers } from '../../../styles/type';
import { dotPatternStyles } from '../../../styles/backgrounds';

import AccordionItem from './AccordionItem';
import TextRenderer from '../../TextRenderer';
import Background from '../../Background';

const StyledBackground = styled(Background)`
  padding: ${rem(60)} 0px;

  &::before,
  &::after {
    ${dotPatternStyles}
    ${({ pattern }) => pattern && `
        display: none;
    `}
    content: '';
    position: absolute;
    width: ${rem(180)};
    height: ${rem(336)};
  }

  &::before {
    right: 0;
    top: 31%;
  }

  &::after {
    left: 0;
    top: 41%;
  }
`;

const ContentWrapper = styled.div`
  ${containerStyles.regular}
`;

const Content = styled.div`
  position: relative;
  z-index: 1;
  border-radius: ${rem(10)};
  overflow: hidden;
  padding: ${rem(40)};
  background-color: ${colors.white};
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.08), 0px 8px 40px rgba(0, 0, 0, 0.08);
  margin: 0 auto;

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    background-color: ${colors.blue['800']};
    border: 1px solid ${colors.blue['700']};
    box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.16), 0px 2px 8px 0px rgba(0, 0, 0, 0.16), 0px 0px 0px 1px #003967 inset;
  `}

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: ${rem(10)};
    background-color: ${colors.blue['500']};
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: ${rem(24)};
  border-bottom: 1px solid ${colors.gray['200']};
  gap: ${rem(20)};
  flex-wrap: wrap;
`;

const Headline = styled.h2`
  ${headers.h3}
  color: ${colors.blue['800']};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.blue['100']};
  `}
`;

const SearchInput = styled.input`
  width: 100%;
  max-width: ${rem(325)};
  display: block;
  font-size: ${rem(16)};
  padding: ${rem(24)};
  border-radius: ${rem(50)};
  border: 1px solid ${colors.gray['200']};
  background: ${colors.gray['50']};
  color: ${colors.gray['600']};

  ${({ theme }) => theme.pageOverrides?.includes('Dark Mode') && `
    color: ${colors.blue['100']};
    background-color: ${colors.blue['800']};
    border: 1px solid ${colors.blue['700']};
  `}
`;

const Columns = styled.div`
  ${media('>small')} {
    display: grid;
    grid-template-columns: repeat(${({ count }) => count}, 1fr);
    gap: ${rem(40)};
  }
`;

const Column = styled.div`
`;

const chunkArray = (array, size) => {
  const chunkedArray = [];

  for (let i = 0; i < array.length; i += size) {
    chunkedArray.push(array.slice(i, i + size));
  }

  return chunkedArray;
};

const Accordion = ({ headline, items, backgroundColor = 'White', backgroundPattern, columns }) => {
  const data = useStaticQuery(graphql`
    query Accordion {
      allContentfulOptionSet(filter: {name: {eq: "accordion"}}) {
        nodes {
          ...OptionSetFragment
        }
      }
    }
  `);
  const { language } = useUserContext();
  const options = createOptionMap(data.allContentfulOptionSet.nodes, language);
  const [search, setSearch] = useState(null);
  const [searchDebounced, setSearchDebounced] = useState(null);
  const columnCount = columns === '2' ? 2 : 1;

  useDebounce(() => {
    setSearchDebounced(search);
  }, 300, [search]);

  const chunkedItems = useMemo(() => (
    columnCount !== 1
      ? chunkArray(items, Math.ceil(items.length / columnCount)) : null
  ), [items, columnCount]);

  const handleSearchChange = (event) => {
    const { value } = event.target;
    const minLength = ['zh-Hans', 'zh-Hant'].includes(language) ? 1 : 2;

    if (value.length >= minLength) {
      setSearch(event.target.value.toLowerCase());
    } else {
      setSearch(null);
    }
  };

  return (
    <StyledBackground
      color={backgroundColor}
      pattern={backgroundPattern}
      patternVariation={null}
    >
      <ContentWrapper itemScope itemType="https://schema.org/FAQPage">
        <Content backgroundColor={backgroundColor}>
          <Header>
            {headline && (
              <Headline>
                <TextRenderer text={headline} />
              </Headline>
            )}
            <SearchInput
              type="text"
              placeholder={`${options['accordion.search_placeholder']} ${outputIfNoVariables(headline) || ''}`}
              onChange={handleSearchChange}
            />
          </Header>
          {columnCount > 1 ? (
            <Columns count={columnCount}>
              {chunkedItems.map((itemChunk, i) => (
                <Column
                  // eslint-disable-next-line react/no-array-index-key
                  key={i}
                >
                  {itemChunk.map((item, j) => (
                    <AccordionItem
                      // eslint-disable-next-line react/no-array-index-key
                      key={j}
                      headline={item.headline}
                      text={item.text}
                      search={searchDebounced}
                    />
                  ))}
                </Column>
              ))}
            </Columns>
          ) : (
            items?.map((item, index) => (
              <AccordionItem
                // eslint-disable-next-line react/no-array-index-key
                key={index}
                headline={item.headline}
                text={item.text}
                search={searchDebounced}
              />
            ))
          )}
        </Content>
      </ContentWrapper>
    </StyledBackground>
  );
};

Accordion.propTypes = {
  headline: PropTypes.string,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      headline: PropTypes.string,
      text: PropTypes.shape({
        raw: PropTypes.string,
        // eslint-disable-next-line react/forbid-prop-types
        references: PropTypes.arrayOf(PropTypes.object)
      })
    })
  ),
  backgroundColor: PropTypes.oneOf(['White', 'Light Blue']),
  columns: PropTypes.oneOf(['1', '2'])
};

export const query = graphql`
  fragment AccordionFragment on ContentfulAccordion {
    id
    __typename
    headline
    items {
      headline
      text {
        raw
        references {
          ...TextRendererAssetFragment
          ...TextRendererButtonFragment
          ...TextRendererFormFragment
          ...TextRendererTrialFormFragment
          ...TextRendererImageFragment
          ...TextRendererMediaFragment
          ...TextRendererVideoFragment
          ...TextRendererWidgetFragment
        }
      }
    }
    backgroundColor
    backgroundPattern
    columns
    personalizations {
      ...PersonalizationFragment
    }
  }
`;

export default Accordion;
